import { useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useForm } from "hooks/useForm";
import { changePassword, ChangePasswordData, selectProfileErrors } from "features/auth/authSlice";
import { Alert, Box, Button, TextField } from "@mui/material";
import LargeDialog from "components/LargeDialog";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";

export interface ChangePasswordModalProps {
  onClose: () => void;
}

const ChangePasswordDialog: React.FunctionComponent<ChangePasswordModalProps> = (props) => {
  const { onClose } = props;
  const dispatch = useAppDispatch();
  const errorResponse = useAppSelector(selectProfileErrors);
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    handleChange,
    data: passwordData,
    errors,
  } = useForm<ChangePasswordData>({
    validations: {
      oldPassword: {
        custom: {
          isValid: (value) => {
            return value.length > 0;
          },
          message: "Ange ditt gamla lösenord.",
        },
      },
      newPassword: {
        custom: {
          isValid: (value) => {
            return value.length > 5;
          },
          message: "Det nya lösenordet uppfyller inte kraven.",
        },
      },
      confirmNewPassword: {
        custom: {
          isValid: (value) => {
            return value === passwordData.newPassword;
          },
          message: "Lösenorden stämmer inte överens.",
        },
      },
    },
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    onSubmit: async () => {
      setIsLoading(true);
      try {
        await dispatch(changePassword(passwordData)).unwrap();
        toast.success("Lösenordet har ändrats");
      } catch (error) {
        toast.error("Kunde inte ändra lösenordet");
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <LargeDialog
      onClose={onClose}
      title="Ändra lösenord"
      actions={
        <>
          <Button
            variant="outlined"
            onClick={onClose}
            startIcon={<ClearIcon />}
            disabled={isLoading}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            form="change-password-form"
            variant="contained"
            color="primary"
            startIcon={<CheckIcon />}
            loading={isLoading}
            disabled={
              passwordData.oldPassword === "" ||
              passwordData.newPassword === "" ||
              passwordData.confirmNewPassword === ""
            }
          >
            Spara
          </Button>
        </>
      }
    >
      <Box
        component="form"
        id="change-password-form"
        onSubmit={handleSubmit}
        sx={{ display: "grid", gap: 1 }}
      >
        <TextField
          type="password"
          label="Nuvarande lösenord"
          variant="filled"
          value={passwordData.oldPassword}
          onChange={handleChange("oldPassword")}
          error={!!errors.oldPassword}
          helperText={errors.oldPassword}
        />

        <TextField
          type="password"
          label="Nytt lösenord"
          variant="filled"
          value={passwordData.newPassword}
          onChange={handleChange("newPassword")}
          error={!!errors.newPassword}
          helperText={errors.newPassword}
        />

        <TextField
          type="password"
          label="Bekräfta nytt lösenord"
          variant="filled"
          value={passwordData.confirmNewPassword}
          onChange={handleChange("confirmNewPassword")}
          error={!!errors.confirmNewPassword}
          helperText={errors.confirmNewPassword}
        />

        {/* Display server errors - i.e. wrong password */}
        {errorResponse && (
          <Alert severity="error" className="mt-3">
            {errorResponse}
          </Alert>
        )}

        {/* Display validation errors */}
        {errors.oldPassword && (
          <Alert severity="error" className="mt-3">
            {errors.oldPassword}
          </Alert>
        )}

        {errors.newPassword && (
          <Alert severity="error" className="mt-3">
            {errors.newPassword}
          </Alert>
        )}

        {errors.confirmNewPassword && (
          <Alert severity="error" className="mt-3">
            {errors.confirmNewPassword}
          </Alert>
        )}
      </Box>
    </LargeDialog>
  );
};

export default ChangePasswordDialog;
